/**
 * StressTestResults - Component for displaying stress test scenarios
 * Neutral card design — white background, impact numbers in red/green only
 */

import React from 'react';

export interface StressTestScenario {
  name: string;
  impact: number;
  probability?: string;
  description?: string;
  severity?: 'low' | 'medium' | 'high' | 'extreme';
}

export interface StressTestResultsProps {
  scenarios: StressTestScenario[];
  portfolioValue: number;
  title?: string;
  currency?: string;
  showMonetaryImpact?: boolean;
  className?: string;
}

const StressTestResults: React.FC<StressTestResultsProps> = ({
  scenarios,
  portfolioValue,
  title = 'Stress Test Results',
  currency = 'CHF',
  showMonetaryImpact = true,
  className = '',
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (!scenarios || scenarios.length === 0) {
    return (
      <div className={`p-6 text-center text-gray-500 ${className}`}>
        No stress test results available
      </div>
    );
  }

  return (
    <div className={className}>
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}

      <div className="space-y-2">
        {scenarios.map((scenario, index) => {
          const monetaryImpact = (portfolioValue * scenario.impact) / 100;
          const impactColor = scenario.impact >= 0 ? 'text-green-600' : 'text-red-600';

          return (
            <div
              key={index}
              className="flex items-center justify-between py-3 px-4 bg-white border border-gray-200 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 truncate">{scenario.name}</h4>
              </div>

              <div className="text-right ml-4 shrink-0">
                <p className={`text-lg font-bold ${impactColor}`}>
                  {scenario.impact >= 0 ? '+' : ''}{scenario.impact.toFixed(1)}%
                </p>
                {showMonetaryImpact && portfolioValue > 0 && (
                  <p className="text-xs text-gray-500">
                    {formatCurrency(Math.abs(monetaryImpact))}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StressTestResults;
